import React, { useCallback, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport } from "hooks";
import { formatCurrency } from "utils/helpers/accounting";
import { ROUTES } from "utils";
import { useNavigate } from "react-router-dom";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const firstOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
const todayString = formatDate(today);

type InOutQuery = {
  from_date: string;
  to_date: string;
  warehouse_id?: string;
  group_id?: string;
  show_inactive?: boolean;
};

const RpInOutReport: React.FC = () => {
  const navigate = useNavigate();
  const { fetchRpInOut } = useReport();
  const [filters, setFilters] = useState<InOutQuery>({
    from_date: firstOfMonth,
    to_date: todayString,
  });
  const [submitted, setSubmitted] = useState<InOutQuery | null>(null);

  const handleFilterChange = useCallback((key: keyof InOutQuery, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) return;
      setSubmitted(filters);
      fetchRpInOut.mutate({
        from_date: filters.from_date,
        to_date: filters.to_date,
        group_id: filters.group_id ? Number(filters.group_id) : undefined,
        show_inactive: filters.show_inactive,
      });
    },
    [fetchRpInOut, filters],
  );

  const records = (fetchRpInOut.data?.data?.records ?? fetchRpInOut.data?.data ?? []) as any[];

  const totals = useMemo(
    () =>
      records.reduce(
        (acc, r) => {
          acc.opening_qty += Number(r.opening_qty ?? 0) || 0;
          acc.opening_amount += Number(r.opening_amount ?? 0) || 0;
          acc.import_qty += Number(r.import_qty ?? 0) || 0;
          acc.import_amount += Number(r.import_amount ?? 0) || 0;
          acc.export_qty += Number(r.export_qty ?? 0) || 0;
          acc.export_amount += Number(r.export_amount ?? 0) || 0;
          acc.closing_qty += Number(r.closing_qty ?? 0) || 0;
          acc.closing_amount += Number(r.closing_amount ?? 0) || 0;
          return acc;
        },
        {
          opening_qty: 0, opening_amount: 0,
          import_qty: 0, import_amount: 0,
          export_qty: 0, export_amount: 0,
          closing_qty: 0, closing_amount: 0,
        },
      ),
    [records],
  );

  const columns = useMemo(
    () => [
      { headerName: "Mã hàng hóa", field: "product_code", pinned: "left", minWidth: 130 },
      { headerName: "Tên hàng hóa", field: "product_name", pinned: "left", minWidth: 200, flex: 1 },
      { headerName: "ĐVT", field: "unit", minWidth: 80 },
      {
        headerName: "Tồn đầu kỳ",
        children: [
          { headerName: "Số lượng", field: "opening_qty", minWidth: 110, valueFormatter: (p: any) => p.value != null ? Number(p.value).toLocaleString("vi-VN") : "", cellClass: "text-right" },
          { headerName: "Thành tiền", field: "opening_amount", minWidth: 130, valueFormatter: (p: any) => p.value != null ? formatCurrency(Number(p.value)) : "", cellClass: "text-right" },
        ],
      },
      {
        headerName: "Nhập trong kỳ",
        children: [
          { headerName: "Số lượng", field: "import_qty", minWidth: 110, valueFormatter: (p: any) => p.value != null ? Number(p.value).toLocaleString("vi-VN") : "", cellClass: "text-right text-blue-700 font-medium" },
          { headerName: "Thành tiền", field: "import_amount", minWidth: 130, valueFormatter: (p: any) => p.value != null ? formatCurrency(Number(p.value)) : "", cellClass: "text-right text-blue-700 font-medium" },
        ],
      },
      {
        headerName: "Xuất trong kỳ",
        children: [
          { headerName: "Số lượng", field: "export_qty", minWidth: 110, valueFormatter: (p: any) => p.value != null ? Number(p.value).toLocaleString("vi-VN") : "", cellClass: "text-right text-red-600 font-medium" },
          { headerName: "Thành tiền", field: "export_amount", minWidth: 130, valueFormatter: (p: any) => p.value != null ? formatCurrency(Number(p.value)) : "", cellClass: "text-right text-red-600 font-medium" },
        ],
      },
      {
        headerName: "Tồn cuối kỳ",
        children: [
          { headerName: "Số lượng", field: "closing_qty", minWidth: 110, valueFormatter: (p: any) => p.value != null ? Number(p.value).toLocaleString("vi-VN") : "", cellClass: "text-right font-semibold text-gray-900" },
          { headerName: "Thành tiền", field: "closing_amount", minWidth: 130, valueFormatter: (p: any) => p.value != null ? formatCurrency(Number(p.value)) : "", cellClass: "text-right font-semibold text-gray-900" },
        ],
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({ sortable: true, resizable: true, filter: "agTextColumnFilter", floatingFilter: true, minWidth: 100 }),
    [],
  );

  const isLoading = fetchRpInOut.isPending;

  return (
    <div className="p-6 space-y-5">
      {isLoading && <LoadingState message="Đang tải báo cáo" description="Vui lòng chờ trong giây lát." />}

      <nav className="text-sm text-blue-600 flex items-center gap-1 flex-wrap">
        <span className="cursor-pointer hover:underline font-medium" onClick={() => navigate(ROUTES.PRIVATE_ROOT.DASHBOARD)}>BÁO CÁO</span>
        <span className="text-gray-400">/</span>
        <span className="text-gray-500 cursor-pointer hover:underline">Sổ sách, báo cáo quản trị</span>
        <span className="text-gray-400">/</span>
        <span className="text-gray-700 font-medium">Báo cáo nhập xuất tồn vật tư</span>
      </nav>

      <h1 className="text-xl font-bold text-gray-900">Báo cáo cân đối nhập xuất tồn</h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Từ ngày <span className="text-red-500">*</span></label>
            <input type="date" value={filters.from_date} onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" required />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Đến ngày <span className="text-red-500">*</span></label>
            <input type="date" value={filters.to_date} onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" required />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Mã kho</label>
            <select value={filters.warehouse_id || ""} onChange={(e) => handleFilterChange("warehouse_id", e.target.value || undefined)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
              <option value="">Lựa chọn...</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Mã nhóm</label>
            <input type="text" value={filters.group_id || ""} onChange={(e) => handleFilterChange("group_id", e.target.value || undefined)}
              placeholder="Lựa chọn..."
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={filters.show_inactive || false}
              onChange={(e) => handleFilterChange("show_inactive", e.target.checked)} className="rounded" />
            Hiển thị hàng hóa ngừng theo dõi
          </label>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={isLoading}
              className="rounded border border-blue-600 px-4 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 transition">
              Xem số liệu
            </button>
            <button type="button" onClick={handleSubmit} disabled={isLoading}
              className="rounded border border-blue-600 px-4 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 transition">
              Xem báo cáo
            </button>
            <button type="button"
              className="rounded border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
              Xuất file Excel
            </button>
          </div>
        </div>
      </form>

      {submitted && records.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Tồn đầu kỳ", value: totals.opening_amount, color: "text-gray-700" },
            { label: "Nhập trong kỳ", value: totals.import_amount, color: "text-blue-700" },
            { label: "Xuất trong kỳ", value: totals.export_amount, color: "text-red-600" },
            { label: "Tồn cuối kỳ", value: totals.closing_amount, color: "text-green-700" },
          ].map((card) => (
            <div key={card.label} className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
              <p className="text-xs text-gray-500">{card.label}</p>
              <p className={`text-base font-bold ${card.color}`}>{formatCurrency(card.value)}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {submitted ? (
          <>
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700">
                {records.length} mặt hàng · {submitted.from_date} → {submitted.to_date}
              </h2>
            </div>
            <AgGridTable rows={records} columns={columns as any} defaultColDef={defaultColDef} pagination={true} autoHeight />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <svg className="w-12 h-12 mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm">Nhấn <strong>Xem số liệu</strong> hoặc <strong>Xem báo cáo</strong> để tải dữ liệu</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RpInOutReport;
